// Navbar — global toppmeny med navigation, dark mode toggle och auth-state.
// Synlig på alla sidor. Responsiv med hamburger-meny på mobil.

import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export default function Navbar() {
  const { user, isLoading, logout } = useAuth();
  const { theme, isDark, setTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Stäng user-dropdown vid klick utanför
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) {
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }
  }, [userMenuOpen]);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-medium transition ${
      isActive
        ? 'text-indigo-600 dark:text-indigo-400'
        : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
    }`;

  return (
    <header className="sticky top-0 z-40 h-16 border-b border-slate-200 bg-white/80 backdrop-blur dark:border-slate-800 dark:bg-slate-950/80">
      <nav className="mx-auto flex h-full max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
        {/* Brand */}
        <Link
          to="/"
          className="text-lg font-bold tracking-tight text-slate-900 dark:text-white"
        >
          Investen
        </Link>

        {/* Desktop nav links */}
        <div className="hidden items-center gap-6 md:flex">
          <NavLink to="/" end className={linkClass}>
            Home
          </NavLink>
          <NavLink to="/screener" className={linkClass}>
            Screener
          </NavLink>
          <NavLink to="/watchlist" className={linkClass}>
            Watchlist
          </NavLink>
        </div>

        {/* Höger sida */}
        <div className="flex items-center gap-2">
          {/* Dark mode toggle */}
          <button
            type="button"
            onClick={() => {
              if (theme === 'system') {
                // Växla från system till motsatt tema
                setTheme(isDark ? 'light' : 'dark');
              } else {
                setTheme(theme === 'dark' ? 'light' : 'dark');
              }
            }}
            className="rounded p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            title={
              isDark
                ? 'Växla till ljust läge'
                : 'Växla till mörkt läge'
            }
          >
            {isDark ? '☀️' : '🌙'}
          </button>

          {/* Auth — desktop */}
          <div className="hidden md:block">
            {isLoading ? (
              <div className="h-8 w-20 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
            ) : user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 rounded px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <span className="max-w-[160px] truncate">{user.email}</span>
                  <span className="text-xs">▾</span>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-1 w-48 rounded border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <Link
                      to="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Inställningar
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                    >
                      Logga ut
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="rounded px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Logga in
                </Link>
                <Link
                  to="/register"
                  className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  Skapa konto
                </Link>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="rounded p-2 text-slate-600 hover:bg-slate-100 md:hidden dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label="Toggle menu"
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden dark:border-slate-800 dark:bg-slate-950">
          <div className="flex flex-col gap-3">
            <NavLink
              to="/"
              end
              className={linkClass}
              onClick={() => setMenuOpen(false)}
            >
              Home
            </NavLink>
            <NavLink
              to="/screener"
              className={linkClass}
              onClick={() => setMenuOpen(false)}
            >
              Screener
            </NavLink>
            <NavLink
              to="/watchlist"
              className={linkClass}
              onClick={() => setMenuOpen(false)}
            >
              Watchlist
            </NavLink>
            <div className="my-1 border-t border-slate-200 dark:border-slate-800" />
            {isLoading ? (
              <div className="h-8 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
            ) : user ? (
              <>
                <div className="px-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                  {user.email}
                </div>
                <Link
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="rounded px-2 py-1 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Inställningar
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded px-2 py-1 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400"
                >
                  Logga ut
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="rounded px-2 py-1 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Logga in
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMenuOpen(false)}
                  className="rounded bg-indigo-600 px-2 py-1 text-sm font-medium text-white"
                >
                  Skapa konto
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
